import React, {useEffect, useState} from "react";
import {useLocation, useNavigate} from "react-router-dom";
import {
  Box,
  Container,
  CircularProgress,
  Alert,
  useTheme,
  useMediaQuery,
  Snackbar,
} from "@mui/material";
import {CalendarMonth as CalendarIcon} from "@mui/icons-material";
import SessionCalendar from "../../components/Calendar/SessionCalendar";
import SessionDetailModal from "../../components/Calendar/SessionDetailModal";
import SessionFilters, {
  SessionFilters as SessionFiltersType,
} from "../../components/Calendar/SessionFilters";
import CharacterSelectionModal from "../../components/CharacterSelectionModal";
import {useAuth} from "../../AuthContext";
import {useCalendarSessions} from "../../hooks/useFirestore";
import {SessionService} from "../../services/api/sessionService";
import "../../styles/env.css";
import {
  convertToDate,
  getAmsterdamDateISO,
  getAmsterdamTimeHHMM,
} from "../../utils/dateUtils";

// (Removed error boundary used for temporary diagnostics)

interface SessionEvent {
  id: string;
  title: string;
  start: Date;
  end: Date;
  resource: any;
}

const Calendar: React.FC = () => {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();
  const location = useLocation();
  const {user, isDM, isAdmin} = useAuth();

  // Filter state
  const [filters, setFilters] = useState<SessionFiltersType>({
    titanId: "",
    minLevel: 1,
    maxLevel: 20,
  });
  const [filtersExpanded, setFiltersExpanded] = useState(false);

  // Use Firestore integration for real-time session data with filters
  const {events, loading, error} = useCalendarSessions(filters);

  const [selectedSession, setSelectedSession] = useState<any>(null);
  const [modalOpen, setModalOpen] = useState(false);
  const [characterSelectionOpen, setCharacterSelectionOpen] = useState(false);
  const [pendingJoinSessionId, setPendingJoinSessionId] = useState<
    string | null
  >(null);
  const [notification, setNotification] = useState<{
    message: string;
    severity: "success" | "error";
  } | null>(null);
  const [selectedDaySessions, setSelectedDaySessions] = useState<any[] | null>(
    null
  );
  const [dayModalOpen, setDayModalOpen] = useState(false);
  const [focusedDate, setFocusedDate] = useState<Date | undefined>(undefined);

  // Deep link: open specific session by ID if provided in query string
  useEffect(() => {
    const searchParams = new URLSearchParams(location.search);
    const sessionId = searchParams.get("sessionId");
    if (!sessionId || events.length === 0) return;

    const match = events.find((e) => e.resource?.id === sessionId);
    if (match) {
      setSelectedSession(match.resource);
      setModalOpen(true);
      // Focus the calendar on the session's day
      const dateObj = convertToDate(match.resource?.date);
      if (!isNaN(dateObj.getTime())) {
        setFocusedDate(dateObj);
      }
      // Clear query so refresh/share doesn't re-trigger
      navigate("/calendar", {replace: true});
    }
  }, [location.search, events, navigate]);

  const handleEventClick = (event: SessionEvent) => {
    const session = event.resource;

    // Session data already includes formatted time and date from the hook
    setSelectedSession(session);
    setModalOpen(true);
  };

  const handleSlotClick = (slotInfo: any) => {
    // Only allow DMs and Admins to create sessions
    if (!canCreateSessions) {
      return;
    }

    // Extract date from the clicked slot and format it in Europe/Amsterdam
    const clickedDate = new Date(slotInfo.start);
    const formattedDate = getAmsterdamDateISO(clickedDate); // YYYY-MM-DD in Amsterdam
    const formattedTime = getAmsterdamTimeHHMM(clickedDate); // HH:MM in Amsterdam

    // Navigate to session management with pre-filled date/time
    navigate("/sessionmanagement", {
      state: {
        openCreateForm: true,
        prefilledDate: formattedDate,
        prefilledTime: formattedTime,
      },
    });
  };

  const handleJoinSession = async (sessionId: string) => {
    // Open character selection modal
    setPendingJoinSessionId(sessionId);
    setCharacterSelectionOpen(true);
    // Close both modals to ensure character selection modal is visible
    setModalOpen(false);
    setDayModalOpen(false);
    setSelectedDaySessions(null);
  };

  const handleCharacterSelected = async (characterId: string) => {
    if (!pendingJoinSessionId || !user?.uid) return;

    try {
      await SessionService.joinSession({
        sessionId: pendingJoinSessionId,
        playerId: user.uid,
        characterId,
      });

      setNotification({
        message: "Successfully joined session!",
        severity: "success",
      });
      setPendingJoinSessionId(null);
    } catch (error) {
      // Error handling for session join operations
      setNotification({
        message:
          error instanceof Error ? error.message : "Failed to join session",
        severity: "error",
      });
    }
  };

  const handleCharacterSelectionClose = () => {
    setCharacterSelectionOpen(false);
    setPendingJoinSessionId(null);
  };

  const handleLeaveSession = async (sessionId: string) => {
    if (!user?.uid) return;

    try {
      await SessionService.leaveSession({
        sessionId,
        playerId: user.uid,
      });

      setNotification({
        message: "Successfully left session!",
        severity: "success",
      });
      setModalOpen(false);
    } catch (error) {
      // Error handling for session leave operations
      setNotification({
        message:
          error instanceof Error ? error.message : "Failed to leave session",
        severity: "error",
      });
    }
  };

  const handleCloseModal = () => {
    setModalOpen(false);
    setSelectedSession(null);
  };

  const handleShowMore = (events: any[]) => {
    // events: array of SessionEvent (with .resource as session)
    setSelectedDaySessions(events.map((e) => e.resource));
    setDayModalOpen(true);
  };

  // Filter handlers
  const handleFiltersChange = (newFilters: SessionFiltersType) => {
    setFilters(newFilters);
  };

  const handleClearFilters = () => {
    setFilters({
      titanId: "",
      minLevel: 1,
      maxLevel: 20,
    });
  };

  const toggleFilters = () => {
    setFiltersExpanded(!filtersExpanded);
  };

  // Check if user can create sessions (DM or Admin)
  const canCreateSessions = isDM() || isAdmin();

  if (loading) {
    return (
      <Container maxWidth="xl" className="calendar-container">
        <Box
          display="flex"
          justifyContent="center"
          alignItems="center"
          minHeight="400px"
        >
          <CircularProgress />
        </Box>
      </Container>
    );
  }

  if (error) {
    return (
      <Container maxWidth="xl" className="calendar-container">
        <Alert severity="error" className="calendar-error-alert">
          {error}
        </Alert>
      </Container>
    );
  }

  // Empty state when no sessions exist
  if (!loading && events.length === 0) {
    return (
      <Container maxWidth="xl" className="calendar-container">
        <Box className="calendar-empty-container">
          <CalendarIcon className="calendar-empty-icon" />
          <h3 className="calendar-empty-title">No sessions scheduled</h3>
          <p className="calendar-empty-text">
            {canCreateSessions
              ? "Create your first session to get started!"
              : "Check back later for new sessions."}
          </p>
        </Box>
      </Container>
    );
  }

  return (
    <Container maxWidth="xl" className="calendar-container">
      {/* Header with Color Key */}
      <Box className="calendar-header-box">
        <Box className="calendar-header-container">
          <h2 className="env-page-title">Calendar</h2>

          {/* Status Legend */}
          <Box className="calendar-status-legend">
            <Box className="calendar-status-item available">
              <Box className="calendar-status-dot" />
              Available
            </Box>
            <Box className="calendar-status-item signed-up">
              <Box className="calendar-status-dot" />
              Signed Up
            </Box>
            <Box className="calendar-status-item full">
              <Box className="calendar-status-dot" />
              Full
            </Box>
          </Box>
        </Box>

        {/* Create Session Button - moved to toolbar */}
        {/* Removed from here */}
      </Box>

      {/* Session Filters */}
      <SessionFilters
        filters={filters}
        onFiltersChange={handleFiltersChange}
        onClearFilters={handleClearFilters}
        expanded={filtersExpanded}
      />

      {/* Calendar */}
      <Box className="calendar-content-box">
        <SessionCalendar
          events={events}
          onSelectEvent={handleEventClick}
          onSelectSlot={handleSlotClick}
          loading={loading}
          defaultView={isMobile ? "week" : "month"}
          defaultDate={focusedDate}
          onToggleFilters={toggleFilters}
          filtersExpanded={filtersExpanded}
          onShowMore={handleShowMore}
          currentUserId={user?.uid}
        />
      </Box>

      {/* Empty state */}
      {!loading && events.length === 0 && (
        <Box className="calendar-empty-state-box">
          <CalendarIcon className="calendar-empty-icon" />
          <h3 className="calendar-empty-title">No sessions scheduled</h3>
          <p className="calendar-empty-text">
            {canCreateSessions
              ? "Create your first session to get started!"
              : "Check back later for new sessions to join."}
          </p>
        </Box>
      )}

      {/* Session Detail Modal (single session) */}
      <SessionDetailModal
        open={modalOpen}
        onClose={handleCloseModal}
        session={selectedSession}
        onJoinSession={handleJoinSession}
        onLeaveSession={handleLeaveSession}
      />

      {/* Session Detail Modal (multiple sessions for a day) */}
      <SessionDetailModal
        open={dayModalOpen}
        onClose={() => {
          setDayModalOpen(false);
          setSelectedDaySessions(null);
        }}
        sessions={selectedDaySessions || []}
        onJoinSession={handleJoinSession}
        onLeaveSession={handleLeaveSession}
      />

      {/* Character Selection Modal */}
      <CharacterSelectionModal
        isOpen={characterSelectionOpen}
        onClose={handleCharacterSelectionClose}
        onCharacterSelect={handleCharacterSelected}
        session={
          pendingJoinSessionId
            ? (events.find((e) => e.resource.id === pendingJoinSessionId)
                ?.resource as any)
            : undefined
        }
      />

      {/* Notifications */}
      <Snackbar
        open={notification !== null}
        autoHideDuration={6000}
        onClose={() => setNotification(null)}
        anchorOrigin={{vertical: "bottom", horizontal: "center"}}
      >
        <Alert
          onClose={() => setNotification(null)}
          severity={notification?.severity}
          className="calendar-alert-full-width"
        >
          {notification?.message}
        </Alert>
      </Snackbar>
    </Container>
  );
};

export default Calendar;
